import { SxProps } from '@mui/material';
import { DEFAULT_BACKDROP_STYLE, DEFAULT_LINKS_STYLE } from '@wp-block-builder/defaults';
import { TTemplate } from '@wp-block-builder/package';

import { useDocumentStoreContext } from './context';
import useTemplatedColor from './useTemplatedColor';

export const GLOBAL_CSS_PROPERTIES = {
  fontWeight: '400',
  fontSize: '16px',
  padding: '32px 0',
  margin: '0',
  letterSpacing: '0.15008px',
  lineHeight: '1.5',
  'h1, h2, h3,h4, h5, h6': {
    mt: 5,
    mb: 2,
    fontWeight: 'normal',
  },
  h1: {
    fontSize: '32px',
  },
  h2: {
    fontSize: '24px',
  },
  h3: {
    fontSize: '20px',
  },
  hr: {
    m: 0,
  },
  blockquote: {
    my: 4,
    ml: 0,
    pl: 2,
    py: 1,
    borderLeft: '3px solid',
    borderColor: 'grey.200',
  },
  'blockquote > p:last-child': {
    marginBottom: 0,
  },
  'blockquote > p:first-child': {
    marginTop: 0,
  },
} satisfies SxProps;

export function useGlobalConfiguration() {
  const { configuration, layoutConfiguration } = useDocumentStoreContext();
  return layoutConfiguration || configuration!;
}

export function useLegacyTemplatesEnabled() {
  const { configuration } = useDocumentStoreContext();
  return configuration.legacyTemplatesEnabled;
}

export function useGlobalTextColor() {
  const { backdropStyle } = useGlobalConfiguration();

  return useTemplatedColor({
    defaultColor: DEFAULT_BACKDROP_STYLE.color,
    color: backdropStyle.color,
    template: backdropStyle.colorTemplate,
  });
}

export function useGlobalLinkColor() {
  const c = useGlobalConfiguration();
  return useTemplatedColor({
    defaultColor: DEFAULT_LINKS_STYLE.color,
    color: c.linkStyle.color,
    template: c.linkStyle.colorTemplate,
  });
}

export function getGlobalLinkColor(template: TTemplate) {
  return template.linkStyle.color || DEFAULT_LINKS_STYLE.color;
}

export function useGlobalBodyBackgroundColor() {
  const { backdropStyle } = useGlobalConfiguration();
  return useTemplatedColor({
    defaultColor: DEFAULT_BACKDROP_STYLE.backgroundColor,
    color: backdropStyle.backgroundColor,
    template: backdropStyle.backgroundColorTemplate,
  });
}

export function useGlobalFontFamily() {
  const { backdropStyle } = useGlobalConfiguration();
  return backdropStyle.fontFamily ?? DEFAULT_BACKDROP_STYLE.fontFamily;
}
